import React, { useEffect } from 'react';
import TextField from "../../../../../controls/TextField";
import { Controller, useFormContext } from "react-hook-form";
import DateField from '../../../../../controls/Date';
import moment, { Moment } from 'moment';
import Autocomplete from "../../../../../controls/Autocomplete";
import { Grid } from "@mui/material";
import { PreReqsArr } from '../../../../../constants/data_arrays';

type Props = {}

const BaseModelForm: React.FC<Props> = () => {
    const { register, errors, setValue, watch, getValues, trigger } = useFormContext();
    const { activeStart, activeEnd, preReqs } = watch();

    useEffect(() => {
        register({ name: 'activeStart' }, {
            required: 'Required',
            validate: (d: Moment) => {
                const end = getValues('activeEnd');
                return !end || moment(d).isBefore(end) || 'Start Date must be earlier than End Date';
            }
        });
        register({ name: 'activeEnd' }, {
            required: 'Required',
            validate: (d: Moment) => {
                const start = getValues('activeStart');
                return !start || moment(d).isAfter(start) || 'End Date must be later than Start Date';
            }
        });
        register({ name: 'preReqs' });
    }, [register, getValues]);

    return <>
        <Grid container alignContent={'flex-start'} justifyContent={'center'} direction={'row'} spacing={2}>
            <Grid item xs={12} sm={10}>
                <TextField
                    fullWidth name={'name'}
                    label={'Name'}
                    inputRef={register({ required: 'Required' })}
                    error={Boolean(errors.name)}
                    helperText={errors.name?.message || ' '} />
            </Grid>
            <Grid item xs={12} sm={5}>
                <Controller
                    name={'activeStart'}
                    defaultValue={activeStart}
                    rules={{
                        required: 'Required',
                        validate: (d: Moment) => {
                            const end = getValues('activeEnd');
                            return !end || moment(d).isBefore(end) || 'Start Date must be earlier than End Date';
                        }
                    }}
                    render={({ ref, onChange, ...p }) => (
                        <DateField
                            fullWidth
                            label={'Active Start'}
                            openTo={'year'}
                            onChange={(m: Moment | null) => {
                                onChange(m);
                                trigger(['activeStart', 'activeEnd']);
                            }}
                            error={Boolean(errors.activeStart)}
                            helperText={errors.activeStart?.message || ' '}
                            {...p}
                        />
                    )}
                />
            </Grid>
            <Grid item xs={12} sm={5}>
                <Controller
                    name={'activeEnd'}
                    defaultValue={activeEnd}
                    rules={{
                        required: 'Required',
                        validate: (d: Moment) => {
                            const start = getValues('activeStart');
                            return !start || moment(d).isAfter(start) || 'End Date must be later than Start Date';
                        }
                    }}
                    render={({ ref, onChange, ...p }) => (
                        <DateField
                            fullWidth
                            label={'Active End'}
                            openTo={'year'}
                            onChange={(m: Moment | null) => {
                                onChange(m);
                                trigger(['activeStart', 'activeEnd']);
                            }}
                            error={Boolean(errors.activeEnd)}
                            helperText={errors.activeEnd?.message || ' '}
                            {...p}
                        />
                    )}
                />
            </Grid>
            <Grid item xs={12} sm={5}>
                <TextField name={'lowAmount'} fullWidth
                    label={'Low Amount'}
                    onChange={() => {
                        trigger(['lowAmount', 'highAmount']);
                    }}
                    inputProps={{
                        ref: register({
                            required: 'Required',
                            validate: (v) => {
                                const value = Number(v);
                                if (isNaN(value) || value < 0) {
                                    return 'Not a valid number';
                                }
                                const highRaw = getValues('highAmount');
                                const highAmount = Number(highRaw);
                                if (highRaw !== undefined && highRaw !== '' && !isNaN(highAmount) && highAmount > 0 && value >= highAmount) {
                                    return 'Low Amount must be less than High Amount';
                                }
                                return true;
                            },
                        }),
                        onChange: () => {
                            trigger(['lowAmount', 'highAmount']);
                        },
                    }} 
                    error={Boolean(errors.lowAmount)}
                    helperText={errors.lowAmount?.message || (errors.lowAmount && 'Not a valid number') || ' '} />
            </Grid>
            <Grid item xs={12} sm={5}>
                <TextField name={'highAmount'} fullWidth
                    label={'High Amount'}
                    onChange={() => {
                        trigger(['lowAmount', 'highAmount']);
                    }}
                    inputProps={{
                        ref: register({
                            required: 'Required',
                            validate: (v) => {
                                const lowRaw = getValues('lowAmount');
                                const lowAmount = Number(lowRaw);
                                const highAmount = Number(v);
                                if (isNaN(highAmount) || highAmount <= 0) {
                                    return 'Not a valid number';
                                }
                                if (lowRaw !== undefined && lowRaw !== '' && !isNaN(lowAmount) && lowAmount >= highAmount) {
                                    return 'High Amount must be greater than Low Amount';
                                }
                                return true;
                            }
                        }),
                        onChange: () => {
                            trigger(['lowAmount', 'highAmount']);
                        },
                    }}
                    error={Boolean(errors.highAmount)}
                    helperText={errors.highAmount?.message || (errors.highAmount && 'Not a valid number') || ' '} />
            </Grid>

            <Grid item xs={12} sm={10}>
                <Autocomplete options={PreReqsArr} label={'Pre Requisites'}
                    name={'preReqs'}
                    fullWidth
                    value={preReqs}
                    onChange={(e, v) => setValue('preReqs', v)}
                    error={Boolean(errors.preReqs)}
                    helperText={errors?.preReqs?.message || ' '}
                    limitTags={5} />
            </Grid>
            <Grid item xs={12} sm={5}>
                <TextField name={'groupID'} fullWidth
                    label={'Group ID'}
                    type="number"
                    inputProps={{ ref: register({ valueAsNumber: true }) }}
                    error={Boolean(errors.groupID)}
                    helperText={errors.groupID?.message || (errors.groupID && 'Not a valid number') || ' '} />
            </Grid>
            <Grid item xs={12} sm={5}>
                <TextField name={'groupWeight'} fullWidth
                    label={'Group Weight'}
                    type="number"
                    inputProps={{ ref: register({ valueAsNumber: true }) }}
                    error={Boolean(errors.groupWeight)}
                    helperText={errors.groupWeight?.message || (errors.groupWeight && 'Not a valid number') || ' '} />
            </Grid>
        </Grid>
    </>;
}

export default BaseModelForm;